import { ImpactType } from '@prisma/client';
import { DateTime } from 'luxon';
import { Currency } from '../locale';
import { fromShortCode } from '../subscriptions';
import { HubSpotCompanyProperties, HubSpotContactProperties, HubSpotInvoiceProperties } from './hubspot-properties';

const NO_ADDONS = 'n/a';
const NO_SUBSCRIPTION_PLAN = 'PAYG';

export type SubscriptionSummary = {
  addons: { shortCode: string; quantity: number }[];
  isPaused: boolean;
  planShortCode: string;
};

export type FundedImpactSummary = {
  fundingIntegrations: string[];
  fundingVariants: string[];
  impactTypes: ImpactType[];
  projectIds?: string | null;
};

export type SpendSource = 'API' | 'ASSISTED' | 'IMPACT_SHOP' | 'SHOPIFY' | 'SUBSCRIPTIONS';

export type SpendBySourceSummary = Partial<
  Record<SpendSource, { amountSpent2y: string | null; lastPurchaseDate: string | null }>
>;

type CommonPropertiesInput = {
  accountType?: string | null;
  hasImpact: boolean;
  lastImpactShopPurchaseDate?: string | null;
  subscription?: SubscriptionSummary;
};

export type ContactPropertiesInput = CommonPropertiesInput & {
  createdAt: Date;
  currency: Currency;
  email: string;
  firstName?: string | null;
  fundedImpact: FundedImpactSummary;
  isSecondaryUser: boolean;
  lastName?: string | null;
  migratedToNewAppAt?: Date | null;
};

export type CompanyPropertiesInput = CommonPropertiesInput & {
  childProfileUsernames?: string | null;
  createdAt?: Date;
  currency?: Currency;
  fundedImpact?: FundedImpactSummary;
  primaryEmail: string;
  parentProfileUsername?: string | null;
  salesAgentName?: string | null;
  spendBySource?: SpendBySourceSummary;
  userId: string;
  username?: string;
};

export type InvoicePropertiesInput = {
  createdAt?: Date | null;
  currency: string;
  id: number;
  orderVariant?: string | null;
  paidAt?: Date | null;
  reference?: string | null;
  status: string;
  totalAmount: number;
  userId: string;
};

/**
 * A key genuinely absent from the input (e.g. `firstname` for a secondary user, deliberately
 * `undefined`) is dropped - HubSpot has no reason to expect it. A key present but null because the
 * underlying fact became empty (e.g. a name was cleared) must still be *sent*, as `''`, so a stale
 * non-empty HubSpot value actually gets cleared - dropping the key here would make that clearing
 * invisible to a reconciling sync that only diffs properties it's told about.
 */
function withoutMissingValues<T extends object>(properties: Record<string, unknown>): T {
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([, value]) => value !== undefined)
      .map(([name, value]) => [name, value === null ? '' : value]),
  ) as T;
}

export function buildAddonsValue(subscription?: SubscriptionSummary): string {
  if (!subscription?.addons.length) {
    return NO_ADDONS;
  }

  return [...subscription.addons]
    .sort((a, b) => a.shortCode.localeCompare(b.shortCode))
    .map(addon => `${fromShortCode(addon.shortCode).code} x ${addon.quantity}`)
    .join(', ');
}

export function buildPlanValue(subscription?: SubscriptionSummary): string {
  return subscription ? fromShortCode(subscription.planShortCode).code : NO_SUBSCRIPTION_PLAN;
}

export function buildImpactCategoriesFundedValue(impactTypes: ImpactType[]): string | undefined {
  const fundedTypes = new Set(impactTypes);
  const categories: string[] = [];

  if (fundedTypes.has(ImpactType.TREES)) categories.push('TREES');
  if (fundedTypes.has(ImpactType.HABITAT_RESTORATION)) categories.push('HABITAT_RESTORATION');
  if (fundedTypes.has(ImpactType.CARBON_OFFSETS)) categories.push('CARBON_OFFSETS');
  if (fundedTypes.has(ImpactType.CARBON_REMOVAL)) categories.push('CARBON_REMOVAL');
  if (fundedTypes.has(ImpactType.TREES) || fundedTypes.has(ImpactType.HABITAT_RESTORATION)) categories.push('NATURE');
  if (fundedTypes.has(ImpactType.CARBON_OFFSETS) || fundedTypes.has(ImpactType.CARBON_REMOVAL))
    categories.push('CARBON');

  return categories.length ? `|${categories.join('|')}|` : undefined;
}

export function buildFundingSourcesValue(variants: string[], integrations: string[]): string | undefined {
  const sources = new Set([...variants, ...integrations]);

  if (integrations.length > 0) sources.add('INTEGRATION');

  return sources.size ? `|${[...sources].sort().join('|')}|` : undefined;
}

function buildCommonProperties(input: CommonPropertiesInput) {
  return {
    account_type: input.accountType,
    has_impact: input.hasImpact ? ('true' as const) : ('false' as const),
    last_impact_shop_purchase_date: input.lastImpactShopPurchaseDate,
    subscription_is_paused: input.subscription?.isPaused ? ('true' as const) : ('false' as const),
    subscription_plan: buildPlanValue(input.subscription),
  };
}

export function buildContactProperties(input: ContactPropertiesInput): HubSpotContactProperties {
  const commonProperties = buildCommonProperties(input);
  const { fundingIntegrations, fundingVariants, impactTypes, projectIds } = input.fundedImpact;

  return withoutMissingValues<HubSpotContactProperties>({
    accountcreatedat: input.createdAt.toISOString(),
    account_type: commonProperties.account_type,
    addons: buildAddonsValue(input.subscription),
    currency: input.currency,
    email: input.email,
    firstname: input.isSecondaryUser ? undefined : input.firstName,
    has_platform_access: 'true',
    lastname: input.isSecondaryUser ? undefined : input.lastName,
    ispaused: commonProperties.subscription_is_paused,
    plan: commonProperties.subscription_plan,
    is_secondary_user: input.isSecondaryUser ? 'true' : 'false',
    profile_has_impact: commonProperties.has_impact,
    migrated_to_new_app_date: input.migratedToNewAppAt
      ? DateTime.fromJSDate(input.migratedToNewAppAt).toISODate() ?? undefined
      : undefined,
    last_impact_shop_purchase_date: commonProperties.last_impact_shop_purchase_date,
    funded_project_ids: projectIds,
    impact_categories_funded: buildImpactCategoriesFundedValue(impactTypes),
    funding_sources: buildFundingSourcesValue(fundingVariants, fundingIntegrations),
  });
}

export function buildCompanyProperties(input: CompanyPropertiesInput): HubSpotCompanyProperties {
  const fundedImpactProperties = input.fundedImpact
    ? {
        funded_project_ids: input.fundedImpact.projectIds ?? null,
        impact_categories_funded: buildImpactCategoriesFundedValue(input.fundedImpact.impactTypes) ?? null,
        funding_sources:
          buildFundingSourcesValue(input.fundedImpact.fundingVariants, input.fundedImpact.fundingIntegrations) ?? null,
      }
    : {};

  return withoutMissingValues<HubSpotCompanyProperties>({
    ...buildCommonProperties(input),
    accountcreatedat: input.createdAt?.toISOString(),
    currency: input.currency,
    ...fundedImpactProperties,
    last_purchase_date_shopify: input.spendBySource?.SHOPIFY?.lastPurchaseDate,
    last_purchase_date_api: input.spendBySource?.API?.lastPurchaseDate,
    last_purchase_date_subscriptions: input.spendBySource?.SUBSCRIPTIONS?.lastPurchaseDate,
    last_purchase_date_impact_shop: input.spendBySource?.IMPACT_SHOP?.lastPurchaseDate,
    last_purchase_date_assisted: input.spendBySource?.ASSISTED?.lastPurchaseDate,
    amount_spent_2y_shopify: input.spendBySource?.SHOPIFY?.amountSpent2y,
    amount_spent_2y_api: input.spendBySource?.API?.amountSpent2y,
    amount_spent_2y_subscriptions: input.spendBySource?.SUBSCRIPTIONS?.amountSpent2y,
    amount_spent_2y_impact_shop: input.spendBySource?.IMPACT_SHOP?.amountSpent2y,
    amount_spent_2y_assisted: input.spendBySource?.ASSISTED?.amountSpent2y,
    parent_profile_username: input.parentProfileUsername,
    child_profile_usernames: input.childProfileUsernames,
    user_id: input.userId,
    primary_email: input.primaryEmail,
    username: input.username,
    sales_agent_name: input.salesAgentName,
  });
}

const INVOICE_STATUS_MAP: Record<string, HubSpotInvoiceProperties['hs_invoice_status']> = {
  Paid: 'paid',
  Void: 'voided',
  Pending: 'open',
  Failed: 'open',
};

/** The mapping crm-sync's invoice step reuses to set `hs_invoice_status` outside of a full property derivation. */
export function mapInvoiceStatusToHubSpot(status: string): HubSpotInvoiceProperties['hs_invoice_status'] {
  return INVOICE_STATUS_MAP[status] ?? 'open';
}

export function buildInvoiceProperties(input: InvoicePropertiesInput): HubSpotInvoiceProperties {
  return withoutMissingValues<HubSpotInvoiceProperties>({
    hs_number: input.reference ?? `INV-${input.id}`,
    hs_amount_billed: (input.totalAmount / 100).toFixed(2),
    hs_currency: input.currency,
    hs_invoice_status: mapInvoiceStatusToHubSpot(input.status),
    // Preserve whichever nullish value came in, rather than collapsing null (known: not paid/dated)
    // to undefined (not derived) - the two mean different things to withoutMissingValues above.
    hs_payment_date: input.paidAt ? input.paidAt.toISOString() : input.paidAt,
    hs_invoice_date: input.createdAt ? input.createdAt.toISOString() : input.createdAt,
    ecologi_invoice_id: input.id.toString(),
    ecologi_user_id: input.userId,
    ecologi_order_variant: input.orderVariant,
  });
}
